import get from 'lodash/get';
import pick from 'lodash/pick';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import Stack from '@mui/material/Stack';

import './ProfileSettingsScreen.scss';
import CustomBox from '../common/CustomBox';
import CustomSelect from '../common/CustomSelect';
import CustomTextField from '../common/CustomTextField';
import FileUploadInput from '../common/FileUploadInput';
import MultiTextField from '../common/MultiTextField';
import HtmlEditor from '../common/HtmlEditor';
import QRCodeButton from '../common/QRCodeButton';
import StateSelect from '../common/StateSelect';
import * as formValidations from '../../helpers/validations';
import { WhitePersonIcon } from '../../styles/icons';
import useIsMobile from "../../hooks/useIsMobile";
import { getProviderUrl } from '../../helpers/providers';
import { getLocalTimeZone, getTimeZoneOptions } from '../../helpers/scheduling';

const getFields = ({ highlights = [] } = {}) => ([
  {
    name: 'profile_picture',
    label: 'Profile Picture',
    type: 'file',
    mimeType: 'image/png',
    helperText: 'File must be a .png image.',
    placeholder: null,
    required: false,
    validate: () => true,
    isFullWidth: true,
  },
  {
    name: 'first_name',
    label: 'First Name',
    type: 'text',
    placeholder: 'Enter first name...',
    required: true,
    validate: formValidations.name,
    isFullWidth: false,
  },
  {
    name: 'last_name',
    label: 'Last Name',
    type: 'text',
    placeholder: 'Enter last name...',
    required: true,
    validate: formValidations.name,
    isFullWidth: false,
  },
  {
    name: 'title',
    label: 'Professional Title',
    type: 'options',
    placeholder: 'Select an option',
    required: true,
    validate: () => true,
    options: [
      { value: 'DPT', label: 'Doctor of Physical Therapy' },
      { value: 'PT', label: 'Physical Therapist' },
      { value: 'T', label: 'Personal Trainer' },
    ],
    isFullWidth: false,
  },
  {
    name: 'rate',
    label: 'General Session Rate / Hour',
    type: 'number',
    placeholder: 'Enter price',
    InputProps: { inputProps: { type: 'text', min: '0', max: '999', step: '1', maxlength: '3' } },
    required: true,
    validate: () => true,
    isFullWidth: false,
    // TODO
    // validate: str => /^[0-9]{10,10}/.test(str),
  },
  {
    name: 'primary_location_city',
    label: 'Primary Location - City',
    type: 'text',
    placeholder: 'Arlington',
    required: true,
    validate: str => (str || '').length,
    isFullWidth: false,
  },
  {
    name: 'primary_location_state',
    label: 'Primary Location - State',
    type: 'state',
    required: true,
    validate: () => true,
    isFullWidth: false,
  },
  {
    name: 'timezone',
    label: 'Select your time zone',
    displayName: 'Time zone',
    type: 'options',
    placeholder: null,
    appendDivider: false,
    required: false,
    requiredForScheduling: true,
    hideIfAvailabilityHidden: true,
    validate: () => true,
    options: getTimeZoneOptions(),
    isFullWidth: false,
  },
  {
    name: 'highlights',
    label: 'Highlights',
    type: 'options',
    placeholder: 'Add highlights',
    required: true,
    multiple: true,
    validate: () => true,
    filterSelectedOptions: true,
    disableCloseOnSelect: true,
    options: highlights
      .filter(h => h.category === 'HIGHLIGHT')
      .map(h => ({ value: h.id, label: h.title })),
    isFullWidth: true,
  },
  {
    name: 'location_strings',
    label: 'Where I Treat',
    type: 'multitext',
    placeholder: 'City (ex: Arlington)',
    helperText: 'To help patients narrow in on specific regions that you serve within your primary city and state, please list all the neighborhoods you are likely to travel to. For example, within Portland you may serve Lake Oswego, Sellwood, NE Portland, Downtown, Milwaukie, etc.',
    required: false,
    validate: () => true,
    isFullWidth: true,
  },
  {
    name: 'map_picture',
    label: 'Treatment City Map',
    type: 'file',
    mimeType: 'image/png',
    helperText: 'Please coordinate with the MX marketing team to receive your custom-designed map. Image must be a .png file.',
    placeholder: null,
    required: false,
    validate: () => true,
    isFullWidth: true,
  },
  // {
  //   name: 'radius',
  //   label: 'Service Radius (miles)',
  //   type: 'number',
  //   helperText: 'How far are you willing to travel to see patients?',
  //   placeholder: '20',
  //   required: false,
  //   validate: () => true,
  //   InputProps: { inputProps: { type: 'text', min: '0', max: '99', step: '1', maxlength: '2'} },
  // },
  {
    name: 'what_i_treat',
    label: 'What I Treat',
    type: 'options',
    placeholder: 'Enter conditions',
    required: false,
    multiple: true,
    validate: () => true,
    filterSelectedOptions: true,
    disableCloseOnSelect: true,
    options: highlights
      .filter(h => h.category === 'WHAT_I_TREAT')
      .map(h => ({ value: h.id, label: h.title })),
    isFullWidth: true,
  },
  {
    name: 'who_i_treat',
    label: 'Who I Treat',
    type: 'multitext',
    placeholder: 'Athletes',
    helperText: 'Examples: Runners, Weight Lifters, Young Professionals',
    required: false,
    validate: () => true,
    isFullWidth: true,
  },
  {
    name: 'how_i_treat',
    label: 'How I Treat',
    type: 'options',
    placeholder: 'Enter techniques',
    required: false,
    multiple: true,
    validate: () => true,
    filterSelectedOptions: true,
    disableCloseOnSelect: true,
    options: highlights
      .filter(h => h.category === 'HOW_I_TREAT')
      .map(h => ({ value: h.id, label: h.title })),
    isFullWidth: true,
  },
  {
    name: 'bio',
    label: 'About',
    type: 'html',
    placeholder: 'Introduce yourself with a short bio...',
    required: false,
    validate: () => true,
    isFullWidth: true,
  },
  {
    name: 'education',
    label: 'Education and Certifications',
    placeholder: 'List your educational background and professional certifications',
    type: 'html',
    required: false,
    validate: () => true,
    isFullWidth: true,
  },
  // {
  //   name: 'location_categories',
  //   label: 'Treatment Locations',
  //   type: 'options',
  //   placeholder: 'Enter locations types',
  //   required: false,
  //   multiple: true,
  //   validate: () => true,
  //   options: getLocationOptions(),
  // },
  {
    name: 'specialty_categories',
    label: 'Specialties',
    type: 'options',
    placeholder: 'Enter specialties',
    required: false,
    multiple: true,
    filterSelectedOptions: true,
    validate: () => true,
    options: [
      { value: 'ORTHOPEDICS', label: 'Orthopedic & Musculoskeletal' },
      { value: 'NEUROLOGIC', label: 'Neurological Conditions' },
      { value: 'SPORTS', label: 'Sports Injuries' },
      { value: 'PELVIC_HEALTH', label: 'Pelvic Health & Pregnancy' },
      { value: 'PEDIATRICS', label: 'Pediatric Conditions' },
      { value: 'VESTIBULAR', label: 'Vestibular Conditions' },
      { value: 'PERSONAL_TRAINING', label: 'Personal Training & Fitness' },
      { value: 'GERIATRICS', label: 'Geriatrics' },
    ],
    isFullWidth: true,
  },
  {
    name: 'fun_facts',
    label: 'Fun Facts',
    type: 'html',
    placeholder: '(Optional) Some fun facts about yourself to display on your profile...',
    required: false,
    validate: () => true,
    isFullWidth: true,
  },
  {
    name: 'instagram_url',
    label: 'Instagram Profile',
    type: 'text',
    placeholder: 'https://www.instagram.com/jane-smith',
    required: false,
    validate: str => !str || str.length > 1,
    isFullWidth: false,
  },
  {
    name: 'linkedin_url',
    label: 'LinkedIn Profile',
    type: 'text',
    placeholder: 'https://linkedin.com/in/jane-smith',
    required: false,
    validate: str => !str || str.length > 1,
    isFullWidth: false,
  },
  {
    name: 'youtube_url',
    label: 'YouTube URL',
    type: 'text',
    placeholder: 'https://www.youtube.com/@JaneSmith',
    required: false,
    validate: str => !str || str.length > 1,
    isFullWidth: false,
  },
]);

const defaultFormValues = getFields().reduce((acc, next) => ({
  ...acc,
  [next.name]: (
    next.name === 'timezone' ? getLocalTimeZone() 
    : next.type === 'options' || next.multiple ? []
    : ''
  ),
}), {});

const FieldInput = props => {
  const [blurred, setBlurred] = useState(false);
  const childProps = {
    ...props,
    error: !props.validate(props.value) && blurred,
    onBlur: () => setBlurred(true),
  };
  if (props.type === 'text') return <CustomTextField {...childProps} />;
  if (props.type === 'options') return <CustomSelect {...childProps} />;
  if (props.type === 'html') return <HtmlEditor {...childProps} />;
  if (props.type === 'number') {
    return (
      <CustomTextField
        type='text'
        {...childProps}
      />
    );
  }
  if (props.type === 'textarea') {
    return <CustomTextField type='textarea' multiline rows={5} {...childProps} />;
  }
  if (props.type === 'multitext') {
    return <MultiTextField {...childProps} />;
  }
  if (props.type === 'state') {
    return <StateSelect {...childProps} />;
  }
  if (props.type === 'file') {
    return <FileUploadInput
      {...childProps}
      fileType={childProps.name}
      accept={{ 'image/*': ['.png'] }}
    />;
  }
  return <></>;
};

const handleFieldUpdate = (formValues, setFormValues) => fieldName => e => {
  let value = get(e, 'target.value', '');
  // const inputType = get(getFields().find(f => f.name === fieldName), 'type');
  // if (inputType === 'options') value = value.map(opt => opt.value);
  const values = {
    ...formValues,
    [fieldName]: value,
  };
  return setFormValues(values);
};

// const checkIfFormIsValid = (formValues, validationDict) => {
//   const containsInvalidValues = Object.keys(validationDict).reduce((acc, k) => (
//     validationDict[k] ? acc : false
//   ), true);
//   return containsInvalidValues;
// };

const parseFormValues = formValues => {
  const parsedForm = Object.keys(formValues).reduce((acc, k) => {
    const field = getFields().find(f => f.name === k);
    const inputType = get(field, 'type');
    let value = (
      inputType === 'options' && field.multiple
        ? (
          formValues[k]
            ? formValues[k].map(opt => opt.value ? opt.value : opt)
            : []
        )
        : get(formValues, k)
    );
    // combine all highlight fields into a single highlight array
    if (['highlights', 'what_i_treat', 'how_i_treat'].includes(k)) {
      return {
        ...acc,
        highlights: acc.highlights ? [...acc.highlights, ...value] : [...value],
      };
    }
    return { ...acc, [k]: value };
  }, {});
  return parsedForm;
};

const mapProviderToFormValues = (provider, highlights) => {
  if (!provider) return null;
  let priorValues = (
    provider
      ? pick(
        provider,
        'first_name', 'last_name', 'title', 'rate',
        'primary_location_city', 'primary_location_state', 'timezone',
        'highlights', 'who_i_treat', 'what_i_treat', 'where_i_treat',
        'location_strings', 'experience_category',
        'location_categories', 'treatment_categories', 'specialty_categories',
        'bio', 'fun_facts', 'education', 'how_i_treat',
        'instagram_url', 'linkedin_url', 'youtube_url',
      )
      : null
  );
  if (!priorValues.highlights) priorValues.highlights = [];
  priorValues = {
    ...priorValues,
    highlights: priorValues.highlights
      .filter(hId =>
        get(highlights.find(h => h.id === hId), 'category') === 'HIGHLIGHT'
      ),
      // .map(hId => ({
      //   value: hId,
      //   label: get(highlights.find(h => h.id === hId), 'title'),
      // })),
    what_i_treat: priorValues.highlights.filter(hId =>
      get(highlights.find(h => h.id === hId), 'category') === 'WHAT_I_TREAT'
    ),
    how_i_treat: priorValues.highlights.filter(hId =>
      get(highlights.find(h => h.id === hId), 'category') === 'HOW_I_TREAT'
    ),
    location_categories: priorValues.location_categories ? priorValues.location_categories : [],
    treatment_categories: priorValues.treatment_categories ? priorValues.treatment_categories : [],
    specialty_categories: priorValues.specialty_categories ? priorValues.specialty_categories : [],
  };
  return priorValues;
};

const ProfileSettingsScreen = props => {
  const navigate = useNavigate();
  const onBack = () => navigate(-1);
  const isMobile = useIsMobile();
  const { highlights, provider, onSubmit } = props;
  const priorValues = mapProviderToFormValues(provider, highlights);
  const defaultValues = priorValues || defaultFormValues;
  // const [formValues, setFormValues] = useState(priorValues || defaultFormValues);
  const [formValues, setFormValues] = useState(defaultValues);
  const highlightOptions = (highlights || []).map(h => ({
    ...h,
    value: h.id,
    label: h.title,
  }));

  return (
    <Box className='ProfileSettingsScreen'>
      <Box className='ProfileSettings__head'>
        <Box className='ProfileSettings__head__nav'>
          <Button onClick={onBack}>
            <ChevronLeft />
            Back
          </Button>
        </Box>
        <Box className='ProfileSettings__head__info-box'>
          <Typography variant='extraLarge' color='text.primary' lineHeight='1.875rem'>
            Profile Information
          </Typography>
          <Box className='ProfileSettings__head__line'></Box>
        </Box>
      </Box>
      <CustomBox borderless>
        <Box>
          <Box className='ProfileSettings__content'>
            <Typography 
              variant='extraLarge500Inter' 
              color='text.primary'
              lineHeight='normal'
              component='p' 
              className='ProfileSettings__content__description'
            >
              This will be displayed on your public profile.
            </Typography>
            <Box>
              <Stack direction='row' spacing={0.5} justifyContent='flex-end'>
                <Button
                  variant='secondaryGradient'
                  size={isMobile ? 'medium' : 'large'}
                  onClick={() => navigate(getProviderUrl(provider))}
                  startIcon={<WhitePersonIcon />}
                >
                  View Profile
                </Button>
                <QRCodeButton
                  variant='primaryGradient'
                  description='This QR code will link to your public profile.'
                  size={isMobile ? 'medium' : 'large'}
                  value={'https://' + window.location.hostname + '/#' + getProviderUrl(provider)} 
                />
              </Stack>
            </Box>
          </Box>

          <Box className='ProfileSettings__form'>
            <Grid container spacing={2}>
              {getFields({ highlights: highlightOptions }).map((f, i) =>
                <Grid key={i} item xs={12} md={f.isFullWidth ? 12 : 6}>
                  <FieldInput
                    fullWidth
                    value={get(formValues, f.name)}
                    defaultValue={get(defaultValues, f.name)}
                    onChange={handleFieldUpdate(formValues, setFormValues)(f.name)}
                    src={
                      f.name === 'map_picture'
                        ? provider.public_map_img_url
                        : provider.public_profile_img_url
                    }
                    {...f}
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <Box className='ProfileSettings__form__footer'>
                  <Button
                    variant='primaryGradient'
                    sx={{minWidth: '164px'}}
                    onClick={() => onSubmit(parseFormValues(formValues))}
                  >
                    Save
                  </Button>
                </Box>
              </Grid>
            </Grid>

          </Box>
        </Box>
      </CustomBox>
    </Box>
  )
};

export default ProfileSettingsScreen;
